import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Star,
  History,
  Briefcase,
  X,
} from 'lucide-react';
import styles from './Sidebar.module.css';
import NavItem from './NavItem';
import LinkListSection from './LinkListSection';
import SidebarSection from './SidebarSection';
import { useProfile } from '../../../Providers/ProfileProvider';
import { useLinks } from '../../../Providers/LinksProvider';
import NavItem2 from './NavItem2';
import CloseButton from '../../buttons/CloseButton';
import Popup from '../../popup/Popup';
import ContentJoinWorkspacePopup from '../../popup/ContentJoinWorkspacePopup';
import ProfileWorkspaceSelect from '../../select/ProfileWorkspaceSelect';
import HelpMenu from '../../select/HelpMenu';
import {
  checkInviteToken,
  acceptInvitation,
  declineInvitation,
} from '../../../apiServices/invite/invite';

export default function Sidebar({
  viewList,
  open,
  onClose,
}) {
  const { t } = useTranslation();
  const width = open ? 240 : 0;
  const { profile, setProfile } = useProfile();
  const {
    favorites, visits, isFavorite, toggleFavorite,
  } = useLinks();

  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 900px)').matches);
  const [joinPopupOpen, setJoinPopupOpen] = useState(false);
  const [inviteDetails, setInviteDetails] = useState({ workspaceName: '', roleName: '' });
  const [expandedSections, setExpandedSections] = useState({
    myWork: true,
    favorites: true,
    history: true,
  });
  const toggleSection = (key) => setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const token = profile?.config?.inviteToken;
    if (!token) return;
    checkInviteToken({ token }).then((res) => {
      if (res?.data?.valid) {
        setInviteDetails({
          workspaceName: res.data.workspaceName || '',
          roleName: res.data.roleName || '',
        });
        setJoinPopupOpen(true);
      } else {
        declineInvitation().then(() => {
          setProfile((prev) => {
            const newConfig = { ...(prev.config || {}) };
            delete newConfig.inviteToken;
            return { ...prev, config: newConfig };
          });
        });
      }
    }).catch(() => {});
  }, [profile?.config?.inviteToken]);

  const handleAccept = async () => {
    await acceptInvitation();
    setJoinPopupOpen(false);
    setProfile((prev) => {
      const newConfig = { ...(prev.config || {}) };
      delete newConfig.inviteToken;
      return { ...prev, config: newConfig };
    });
    sessionStorage.removeItem('inviteToken');
  };

  const handleDecline = async () => {
    await declineInvitation();
    setJoinPopupOpen(false);
    setProfile((prev) => {
      const newConfig = { ...(prev.config || {}) };
      delete newConfig.inviteToken;
      return { ...prev, config: newConfig };
    });
    sessionStorage.removeItem('inviteToken');
  };
  // const location = useLocation();
  const currentWorkspace = localStorage.getItem('currentWorkspace');
  const spacerWidth = isMobile ? 0 : width;
  return (
    <>
      {isMobile && open && (
        <div
          className={styles.overlay}
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <div
        style={{
          width: `${spacerWidth}px`,
          height: '100%',
          borderRadius: 'var(--radius-2xl)',
          zIndex: 10,
        }}
      >
        <motion.div
          animate={{ width: `${spacerWidth}px` }}
          transition={{ duration: 0.1, ease: 'easeInOut' }}
          style={{
            height: '100%',
          }}
          className={styles.sideBarPaper}
        />
      </div>
      {open && (
        <motion.div
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          id="sidebar"
          style={{
            width: isMobile ? '100%' : '240px',
            height: '100%',
            boxSizing: 'border-box',
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          layout
          className={styles.sidebarContainer}
          aria-label="Sidebar navigation"
        >
          {isMobile && (
            <div style={{ position: 'absolute', right: '10px', top: '10px' }}>
              <CloseButton onClick={onClose} />
            </div>
          )}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-xs)',
            width: '100%',
          }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <ProfileWorkspaceSelect />
            </div>
            <Link
              to="/home"
              aria-label="octogrey"
              className={styles.octoLink}
              style={{
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
                marginRight: '10px',
                background: 'none',
                padding: '4px',
                borderRadius: '50%',
              }}
            >
              <svg
                viewBox="0 0 512 512"
                width="28"
                height="28"
                role="img"
                aria-label="octogrey"
                className={styles.octoIcon}
                style={{ color: 'var(--visible)', borderRadius: '50%' }}
              >
                <path
                  fill="currentColor"
                  d="M181.4,256.9 A116,116 0 1 1 330.6,256.9 C320.0,270.0 316.0,286.0 322.0,302.0 C328.0,320.0 348.0,322.0 358.0,306.0 C374.0,286.0 388.0,262.0 410.0,248.0 C428.0,236.0 450.0,230.0 470.0,240.0 C452.0,250.0 434.0,262.0 420.0,282.0 C404.0,308.0 380.0,350.0 346.0,376.0 C364.0,392.0 396.0,396.0 426.0,388.0 C452.0,381.0 474.0,400.0 472.0,460.0 C456.0,445.0 438.0,440.0 418.0,444.0 C376.0,454.0 324.0,460.0 290.0,420.0 C276.0,400.0 264.0,384.0 256.0,370.0 C248.0,384.0 236.0,400.0 222.0,420.0 C188.0,460.0 136.0,454.0 94.0,444.0 C74.0,440.0 56.0,445.0 40.0,460.0 C38.0,400.0 60.0,381.0 86.0,388.0 C116.0,396.0 148.0,392.0 166.0,376.0 C132.0,350.0 108.0,308.0 92.0,282.0 C78.0,262.0 60.0,250.0 42.0,240.0 C62.0,230.0 84.0,236.0 102.0,248.0 C124.0,262.0 138.0,286.0 154.0,306.0 C164.0,322.0 184.0,320.0 190.0,302.0 C196.0,286.0 192.0,270.0 181.4,256.9 Z"
                />
              </svg>
            </Link>
          </div>
          <nav aria-label="Main navigation" style={{ display: 'flex', width: '100%' }}>
            <ul
              className={styles.navList}
              style={{
                paddingLeft: '0px',
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              {profile.config?.currentWorkspace && (
              <NavItem2
                key={currentWorkspace}
                path={`/${currentWorkspace}`}
                label={currentWorkspace}
                Logo={Star}
              />
              )}
              {!profile.config?.currentWorkspace && (
              <NavItem2
                key={viewList[0].label}
                path={viewList[0].path}
                label={t('nav.chooseWorkspace')}
                Logo={viewList[0].Logo}
              />
              )}

              {profile.config?.currentWorkspace && (
                <SidebarSection
                  title={t('nav.myWork')}
                  Icon={Briefcase}
                  expanded={expandedSections.myWork}
                  onToggle={() => toggleSection('myWork')}
                >
                  <ul style={{
                    paddingLeft: '0px',
                    listStyle: 'none',
                  }}
                  >
                    {viewList.filter((el, i) => i !== 0).map(({ path, label, Logo }) => (label === 'workspaces'
                      ? (
                        <NavItem
                          key={label}
                          path={path}
                          label={profile.config?.currentWorkspace}
                          Logo={Logo}
                        />
                      ) : (
                        <NavItem
                          key={label}
                          path={`/${currentWorkspace}/${label}`}
                          label={label}
                          Logo={Logo}
                        />
                      )))}
                  </ul>
                </SidebarSection>
              )}

              {profile.config?.currentWorkspace && (
                <SidebarSection
                  title={t('nav.favorites')}
                  Icon={Star}
                  expanded={expandedSections.favorites}
                  onToggle={() => toggleSection('favorites')}
                >
                  <LinkListSection
                    items={favorites}
                    emptyLabel={t('nav.noFavorites')}
                    renderAction={() => ({
                      Icon: X,
                      label: t('nav.removeFavorite'),
                      onClick: (i) => toggleFavorite(i.url, i.label),
                    })}
                  />
                </SidebarSection>
              )}

              {profile.config?.currentWorkspace && (
                <SidebarSection
                  title={t('nav.history')}
                  Icon={History}
                  expanded={expandedSections.history}
                  onToggle={() => toggleSection('history')}
                >
                  <LinkListSection
                    items={visits}
                    emptyLabel={t('nav.noHistory')}
                    renderAction={(item) => {
                      const active = isFavorite(item.url);
                      return {
                        Icon: Star,
                        label: active ? t('nav.removeFavorite') : t('nav.addFavorite'),
                        active,
                        onClick: (i) => toggleFavorite(i.url, i.label),
                      };
                    }}
                  />
                </SidebarSection>
              )}
            </ul>
          </nav>
          <div style={{
            marginTop: 'auto',
            display: 'flex',
            alignItems: 'center',
            boxSizing: 'border-box',
            height: '40px',
            padding: '0 10px',
            gap: 'var(--space-xs)',
            width: '235px',
          }}
          >
            <HelpMenu width="30px" height="30px" />
            {/* <button
              type="button"
              className={styles.hoverButton}
              style={{
                color: 'var(--color-text-secondary)',
                fontWeight: 600,
                borderRadius: 'var(--radius-circle)',
                backgroundColor: 'var(--color-bg)',
                border: '1px solid var(--color-border)',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                padding: 'var(--space-xs)',
                boxSizing: 'border-box',
                textDecoration: 'none',
                cursor: 'pointer',
              }}
              onClick={() => {}}
            >
              <HelpCircle size={25} />
            </button>

            <button
              type="button"
              className={styles.hoverButton}
              style={{
                color: 'var(--color-text-secondary)',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--color-bg)',
                border: '1px solid var(--color-border)',
                boxSizing: 'border-box',
                width: 'auto',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                padding: 'var(--space-md)',
                textDecoration: 'none',
                cursor: 'pointer',
                gap: 'var(--space-md)',
                fontSize: '16px',
              }}
              onClick={() => {}}
            >

              <p style={{
                margin: '0px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: '90px',
                whiteSpace: 'nowrap',
              }}
              >
                Loading sqdfvsdvqsv
              </p>
              <Panda size={25} />
            </button>

            <button
              type="button"
              className={styles.hoverButton}
              style={{
                color: 'var(--color-text-secondary)',
                fontWeight: 600,
                borderRadius: 'var(--radius-circle)',
                backgroundColor: 'var(--color-bg)',
                border: '1px solid var(--color-border)',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 'var(--space-xs)',
                boxSizing: 'border-box',
                textDecoration: 'none',
                cursor: 'pointer',
              }}
              onClick={() => {}}
            >
              <Bell size={25} />
            </button> */}
          </div>

        </motion.div>
      )}
      <Popup isOpen={joinPopupOpen} closePopup={() => {}} noClose>
        <ContentJoinWorkspacePopup
          workspaceName={inviteDetails.workspaceName}
          roleName={inviteDetails.roleName}
          onAccept={handleAccept}
          onDecline={handleDecline}
        />
      </Popup>
    </>
  );
}

Sidebar.propTypes = {
  viewList: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string,
      label: PropTypes.string,
      // eslint-disable-next-line react/forbid-prop-types
      Logo: PropTypes.object,
    }),
  ).isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
